if (!customElements.get("cart-b2b-form-trigger")) {
  customElements.define(
    "cart-b2b-form-trigger",
    class CartB2bFormTrigger extends HTMLElement {
      constructor() {
        super();
        this._onClick = this._onClick.bind(this);
      }

      connectedCallback() {
        this.addEventListener("click", this._onClick);
      }

      disconnectedCallback() {
        this.removeEventListener("click", this._onClick);
      }

      _onClick() {
        const key = this.getAttribute("container");
        if (!key) return;
        const container = document.querySelector(`[data-b2b-container="${key}"]`);
        this.querySelector(".rotate")?.classList.toggle("rotate-180");
        container?.classList.toggle("visible");
      }
    }
  );
}

function flattenApiErrors(errors) {
  const messages = [];
  if (!errors || typeof errors !== "object") return messages;
  for (const key of Object.keys(errors)) {
    const val = errors[key];
    if (Array.isArray(val)) {
      val.forEach((msg) => {
        if (msg != null && msg !== "") messages.push(String(msg));
      });
    } else if (typeof val === "string" && val !== "") {
      messages.push(val);
    }
  }
  return messages;
}

if (!customElements.get("cart-b2b-attributes")) {
  customElements.define(
    "cart-b2b-attributes",
    class CartB2b extends HTMLElement {
      constructor() {
        super();

        this.cartId = null;
        this.isLoading = false;
        this.canSubmit = false;
        this.serverErrorRetryCount = 0;
        this.endpoint = window.theme.b2b_vat_validation.endpoint;
        this.serverErrorMessage = window.theme.b2b_vat_validation.serverError ?? "";

        // Nodes
        this.inputs = [];
        this.cartForm = null;
        this.validationErrorEl = null;
        this.defaultValidationMessageText = "";

        // Events
        this.handleCartFormSubmitBound = this.handleCartFormSubmit.bind(this);
      }

      set isLoading(isLoading) {
        this.classList.toggle("loading", isLoading);
        this._isLoading = isLoading;
      }

      get isLoading() {
        return this._isLoading;
      }

      getValidationPayload() {
        const inputNameValue = {};
        this.inputs.forEach((input) => {
          inputNameValue[input.getAttribute("name")] = input.value;
        });
        return {
          company_id: inputNameValue["attributes[ico]"],
          company_name: inputNameValue["attributes[firma]"],
          email: inputNameValue["attributes[email]"],
          myshopify_domain: Shopify.shop,
          vat: inputNameValue["attributes[dic]"]
        };
      }

      cloneInputsToCartForm() {
        if (!this.cartForm) return;
        this.inputs.forEach((input) => {
          const inputInCartForm = this.cartForm.querySelector(`input[name="${input.name}"]`);

          // If input already exists in the cartForm - update its value
          if (inputInCartForm) {
            inputInCartForm.value = input.value;
          } else {
            // Otherwise - clone input node and append it to the cart form
            const inputClone = input.cloneNode();
            inputClone.value = input.value;
            inputClone.type = "hidden";
            inputClone.removeAttribute("id");
            this.cartForm.appendChild(inputClone);
          }
        });
      }

      countEmptyInputs() {
        let count = 0;

        this.inputs.forEach((input) => {
          if (!input.value) count++;
        });

        return count;
      }

      highlightEmptyInputs() {
        const count = this.countEmptyInputs();
        this.classList.toggle("error", count > 0);

        if (count > 0 && this.validationErrorEl) {
          const root = this.validationErrorEl;
          root.replaceChildren();
          if (this.defaultValidationMessageText) {
            const intro = document.createElement("p");
            intro.textContent = this.defaultValidationMessageText;
            root.appendChild(intro);
          }
        }

        this.inputs.forEach((input) => {
          const label = this.querySelector(`label[for="${input.id}"]`);
          input.classList.toggle("error", !input.value);
          if (label) label.classList.toggle("error", !input.value);
        });

        return { count };
      }

      renderValidationMessage(extraMessages = []) {
        if (!this.validationErrorEl) return;
        const root = this.validationErrorEl;
        root.replaceChildren();
        if (!extraMessages.length) return;
        const ul = document.createElement("ul");
        ul.className = "mt-2 list-disc pl-5";
        extraMessages.forEach((msg) => {
          const li = document.createElement("li");
          li.textContent = msg;
          ul.appendChild(li);
        });
        root.appendChild(ul);
      }

      async sendRequest() {
        const payload = this.getValidationPayload();
        this.isLoading = true;
        try {
          const request = await fetch(this.endpoint, {
            headers: {
              "Content-Type": "application/json",
              "Accept-Language": Shopify.locale
            },
            method: "POST",
            body: JSON.stringify(payload)
          });

          const data = await request.json();
          this.isLoading = false;
          return { data, error: false };
        } catch {
          this.isLoading = false;
          return { data: null, error: true };
        }
      }

      getCheckoutSubmitButton() {
        const id = this.cartId === "cart-drawer-form" ? "cart-drawer-submit" : "cart-footer-submit";
        return document.getElementById(id);
      }

      handleValidationSuccess({ flashSuccess = true } = {}) {
        this.serverErrorRetryCount = 0;
        this.classList.remove("error");
        this.renderValidationMessage([]);
        if (flashSuccess) {
          this.classList.add("success");
          setTimeout(() => this.classList.remove("success"), 3 * 1000);
        }
        this.cloneInputsToCartForm();
        this.canSubmit = true;
        this.getCheckoutSubmitButton()?.click();
      }

      async handleCartFormSubmit(e) {
        if (this.isLoading) {
          e.preventDefault();
          return;
        }

        const payload = this.getValidationPayload();

        // Proceed with the checkout if allowed
        if (this.canSubmit || (!payload.company_name && !payload.company_id && !payload.vat)) {
          this.classList.remove("error");
          this.renderValidationMessage([]);
          return;
        }

        e.preventDefault();

        const emptyInputs = this.highlightEmptyInputs();
        if (emptyInputs.count > 0) return;

        const { data, error } = await this.sendRequest();

        if (error) {
          this.serverErrorRetryCount += 1;
          if (this.serverErrorRetryCount > 3) {
            this.handleValidationSuccess({ flashSuccess: false });
            return;
          }
          this.classList.add("error");
          this.renderValidationMessage([this.serverErrorMessage || "Server error"]);
          return;
        }

        this.serverErrorRetryCount = 0;

        if (data?.errors && Object.keys(data.errors).length > 0) {
          this.classList.add("error");
          this.renderValidationMessage(flattenApiErrors(data.errors));
          return;
        }

        this.handleValidationSuccess();
      }

      connectedCallback() {
        this.validationErrorEl = this.querySelector(".b2b-validation-error-message");
        this.defaultValidationMessageText = this.validationErrorEl?.textContent.trim() ?? "";

        this.cartId = this.getAttribute("data-cart-id");
        if (!this.cartId) {
          console.log("Cart id not found");
          return;
        }

        this.cartForm = document.querySelector(`form[id="${this.cartId}"]`);
        this.inputs = Array.from(this.querySelectorAll("input"));

        if (!this.cartForm) {
          console.error("Cart form not found");
          return;
        }

        this.cartForm.addEventListener("submit", this.handleCartFormSubmitBound);
      }

      disconnectedCallback() {
        if (this.cartForm) {
          this.cartForm.removeEventListener("submit", this.handleCartFormSubmitBound);
        }
      }
    }
  );
}
